import { ListItem, UnorderedList } from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import { ContentWidthContainer } from "../components/ContentWidthContainer";
import { PageHeader } from "../components/PageHeader";
import { Address, AddressBlock, HeaderText, Information } from "../components/StaticPageComponents";

const FIRST_PARAGRAPH = `This Privacy Policy sets out how Breakfree Holidays Ltd (BFH) complies
with the General Data Protection Regulation in regard to what
information we gather and how we use it. You can access this Privacy
Statement from the bottom of every page by clicking on the appropriate
link.`;

export const PrivacyPolicy: React.FC = () => (
  <>
    <Helmet>
      <title>Privacy Policy - Holiday Homes for Sale</title>
      <meta name="description" content={FIRST_PARAGRAPH} />
    </Helmet>

    <PageHeader>Privacy Policy</PageHeader>
    <ContentWidthContainer>
      <HeaderText>OUR COMMITMENT TO YOUR PRIVACY</HeaderText>
      <Information>{FIRST_PARAGRAPH}</Information>
      <Information>
        Breakfree Holidays have structured this website so that you can visit a majority of the website without
        identifying yourself or revealing any personal information. Once you choose to provide us with any information
        by which you can be identified, then you can be assured that it will only be used in accordance with this
        privacy statement. By visiting www.holidayhomes.breakfreeholidays.co.uk you are accepting and consenting to the
        practices described in this Privacy Notice.
      </Information>
      <Information>
        The Data Controller for this information is Breakfree Holidays Ltd, the contact details for Breakfree are:
      </Information>
      <AddressBlock>
        <Address>Breakfree Holidays Ltd</Address>
        <Address>42 Phoenix Court</Address>
        <Address>Hawkins Road</Address>
        <Address>Colchester</Address>
        <Address>CO2 8JY</Address>
      </AddressBlock>
      <Information>
        When booking a holiday via breakfreeholidays.co.uk and its partners, Breakfree Holidays Ltd and its partners
        will each be data controllers of your personal data when you submit your data. Breakfree Holidays Ltd will
        handle your personal data in accordance with this Privacy and Cookies Policy.
      </Information>
      <HeaderText>INFORMATION AND DATA BFH GATHERS AND TRACKS</HeaderText>
      <Information>BFH collects personal information and website usage information.</Information>
      <HeaderText>WHAT IS PERSONAL INFORMATION?</HeaderText>
      <Information>
        Personal information is information about you and from which you can be identified. It can include your name,
        address, contact details and details of your bookings and purchases.
      </Information>
      <Information>
        BFH does not store any financial information i.e. debit/credit card details as payments are taken through third
        party payment gateways such as PayPal. BFH gathers two types of information about users:
      </Information>
      <Information>
        (1) Website tracking: information BFH gathers through aggregated tracking information derived mainly by tallying
        page views throughout our websites. This information allows us to better tailor our content to users’ needs and
        to help our clients, advertisers and sponsors better understand the demographics of our audience.
      </Information>
      <Information>
        (2) Holiday booking information: information submitted on this site to handle booking requests, deliver services
        (i.e. booking confirmations), communicate with you about bookings, services and promotional offers, update our
        records and generally maintain your details.
      </Information>
      <HeaderText>(1) WEBSITE USAGE TRACKING</HeaderText>
      <Information>
        BFH tracks user traffic patterns throughout all our sites. However, we do not correlate this information with
        data about individual users. BFH does break down overall usage statistics according to a user’s domain name,
        browser type, and MIME type by reading this information from the browser string (information contained in every
        user’s browser). We use tracking information to determine which areas of our sites users like and don’t like
        based on traffic to those areas. We do not track what individual users read, but rather how well each page
        performs overall. This helps us continue to build a better service for you.
      </Information>
      <HeaderText>COOKIES</HeaderText>
      <Information>
        We may place a text file called a “cookie” in the browser files of your computer. The cookie itself does not
        contain Personal Information although it will enable us to relate your use of this site to information that you
        have specifically and knowingly provided. But the only personal information a cookie can contain is information
        you supply yourself. A cookie can’t read data off your hard disk or read cookie files created by other sites.
        BFH uses cookies to track user traffic patterns (as described above). You can refuse cookies by turning them off
        in your browser. If you’ve set your browser to warn you before accepting cookies, you will receive the warning
        message with each cookie. You do not need to have cookies turned on to use this site, however, refusing cookies
        may restrict the functionality of the promotions that we offer.
      </Information>
      <HeaderText>GOOGLE ANALYTICS</HeaderText>
      <Information>
        This website uses Google Analytics, a web analytics service provided by Google Ireland Limited (’Google’).
        Google Analytics Premium uses ’cookies’, which are text files placed on your computer, to help the website
        analyse how users use the site. The information generated by the cookies about your use of the website and your
        current IP address will be transmitted by your browser to and will be stored by Google on servers in the United
        States and other countries.
      </Information>
      <Information>
        On behalf of the operator of this website, Google will use this information for the purpose of evaluating your
        use of the website, compiling reports on website activity and providing other services relating to website
        activity and internet usage to the website operator. The IP address collected through Google Analytics will not
        be associated with any other data held by Google. You may refuse the use of cookies by selecting the appropriate
        settings on your browser, however, please note that if you do this you may not be able to use the full
        functionality of this website. You may also stop the transmission of information generated by the cookies about
        your use of the website and of your IP address to Google, by downloading and installing the Google Analytics
        Opt-out Browser add-on available here.
      </Information>
      <Information>
        BFH creates aggregate reports on user demographics and traffic patterns for sponsors and partners. Because we
        don’t track the usage patterns of individual users, a partner or sponsor will never know that a specific user
        clicked their advert/park listing. We will not disclose any information about any individual user except to
        comply with applicable law or valid legal processes or to protect the personal safety of our users or the
        public.
      </Information>
      <HeaderText>(2) HOLIDAY BOOKING INFORMATION</HeaderText>
      <Information>
        To process your Booking, we need to collect certain information from you. We take full responsibility for
        ensuring the security of this information. This information includes:
      </Information>

      <UnorderedList>
        <ListItem>your name, age and address;</ListItem>
        <ListItem>your phone number or mobile number and e-mail address;</ListItem>
        <ListItem>names, ages and date of birth of the members of your party</ListItem>
        <ListItem>
          other information you provide when completing forms (e.g. registration forms), requesting further services or
          speaking with us, including information you provide in connection with your stay in one of the parks.
        </ListItem>
      </UnorderedList>

      <Information>
        Breakfree Holidays will hold your personal data plus will pass this data on to third parties and organisations
        that need to know them so that the Booking can be provided.
      </Information>
      <HeaderText>WHO DO WE SHARE YOUR PERSONAL INFORMATION WITH?</HeaderText>
      <Information>
        In connection with the uses referred to above, we may disclose your personal information to certain third
        parties, including:
      </Information>

      <UnorderedList mb={4}>
        <ListItem>The holiday booking provider.</ListItem>
        <ListItem>
          RedEye International Ltd., a company registered in England and Wales, who processes our data to send out email
          communications.
        </ListItem>
        <ListItem>
          Google and/or Facebook (acting as data processors) for them to use on our behalf in connection with the
          creation of marketing and promotion for us. This may include Google and/or Facebook using socio-demographic
          information about you to identify other people like you, with similar interests to present our marketing to.
          This socio-demographic information is not used for any other purpose nor shared with any other party. No
          information about other individuals identified from these networks is shared with us at any time. Your
          information is never made public and is not shared with any other third party.
        </ListItem>
        <ListItem>
          Feefo, Trustpilot and other third party review administrators who may contact you to ask for your comments on
          the accommodation and/or services that have been provided.
        </ListItem>
        <ListItem>
          External agencies and organisations (including the police and other law enforcement agencies) for the purpose
          of preventing and detecting fraud (including fraudulent transactions) and criminal activity and for the
          assessment and collection of tax;
        </ListItem>
        <ListItem>
          Our insurers and insurance brokers and our and their advisers in the event that a claim is made or could be
          made against us.
        </ListItem>
      </UnorderedList>
      <Information>
        In the event that we sell or buy any business or assets, we may disclose personal information held by us to the
        prospective seller or buyer of such business or assets. If we or substantially all of our assets are acquired by
        a third party (or subject to a reorganisation within our corporate group), personal information held by us will
        be one of the transferred assets.
      </Information>
      <Information>
        We may pass your personal information to third parties if we are under a duty to disclose or share your personal
        information in order to comply with any legal or regulatory obligation (including in connection with a court
        order), or in order to enforce or apply the agreements we have with or otherwise concerning you (including
        agreements between you and us (or one or more of our affiliates)); or to protect our rights, property or safety
        or those of our customers, employees or other third parties.
      </Information>
      <Information>
        You should be aware that, if we are requested by the police or any regulatory or government authority
        investigating suspected illegal activities to access and disclose individually identifiable information
        concerning your activities whilst using our website, we reserve the right to do so. We also reserve the right to
        disclose individually identifiable information to third parties where a complaint arises concerning your use of
        our website, and that use is deemed by us inconsistent with the terms governing the use of our website.
      </Information>
      <HeaderText>HOW LONG WILL WE KEEP YOUR PERSONAL INFORMATION?</HeaderText>
      <Information>
        We will keep your personal information for as long as is necessary given the purposes for which we collected and
        use that information.
      </Information>
      <Information>
        If you provide us with personal information about another person, you must ensure that before you provide us
        with their personal information, you have their agreement to do so and that they are aware of the ways in which
        we use personal information as set out in this policy.
      </Information>
      <Information>
        Breakfree Holidays will hold your personal data for future analysis and marketing purposes. All details, with
        the exception of credit/debit card data, given to Breakfree Holidays at any time will be kept, but only names,
        contact details and the Booking preferences will be used for marketing purposes.
      </Information>
      <HeaderText>HOW DO YOU MANAGE MY INFORMATION?</HeaderText>
      <Information>
        We may from time to time appoint third-party service providers, to help us manage efficient systems within the
        business. Some of these systems may for operational reasons be located in countries overseas including the USA,
        although wherever possible we will keep all data within the United Kingdom.
      </Information>
      <Information>
        We will only transfer your information to service providers who help manage our systems, or overseas, where we
        are satisfied that this is lawful and that adequate levels of protection are in place to protect the integrity
        and security of any information being processed and compliance with applicable privacy laws.
      </Information>
      <HeaderText>MARKETING COMMUNICATIONS</HeaderText>
      <Information>
        We offer a free electronic newsletter to users publishing details of special offers and events. BFH gathers the
        email addresses of users who subscribe. Users may remove themselves from this mailing list by following the link
        provided in every newsletter. Users can subscribe to the newsletters at the time of registration when making a
        holiday booking or by completing the ‘sign up’ field on the website.
      </Information>
      <Information>
        If you subsequently wish to stop receiving marketing from us, you may unsubscribe by sending an email to us at
        enquiries@breakfreeholidays.co.uk and entering ’Unsubscribe’ in the subject line. This will unsubscribe you from
        all marketing communications from us.
      </Information>
      <Information>
        Alternatively, you can unsubscribe from marketing email communications from us by following the unsubscribe
        instructions in each email that we send out. This will only unsubscribe you from marketing emails.
      </Information>
      <Information>
        Even if you choose not to receive marketing from us, we may still use your personal information to provide you
        with important services communications, including communications in relation to any bookings or purchases you
        make.
      </Information>
      <HeaderText>POLLING</HeaderText>
      <Information>
        We may offer interactive polls to users so they can easily share their opinions with other users and see what
        our audience thinks about important issues. Opinions or other responses to polls are aggregated and are not
        identifiable to any particular user. BFH may use a system to “tag” users after they have voted, so they can vote
        only once on a particular question. This tag is not correlated with information about individual users.
      </Information>
      <HeaderText>SURVEYS</HeaderText>
      <Information>
        BFH may occasionally conduct user surveys to better target our content. We sometimes share the aggregated
        demographic information in these surveys with our sponsors, advertisers and partners. We never share any of this
        information about specific individuals with any third party.
      </Information>
      <HeaderText>CALL RECORDING</HeaderText>
      <Information>
        Telephone calls to our contact centre may be recorded for training and monitoring purposes. As stated above, we
        use information that users provide in order to send out electronic newsletters and to enable users to
        participate in promotions, polls and surveys. We send out newsletters to subscribers on a regular schedule
        (depending on the newsletter), and occasionally send out special editions when we think subscribers might be
        particularly interested in something we are doing. BFH never shares newsletter mailing lists with any third
        parties, including advertisers, sponsors or partners, except where users have given their explicit authority.
      </Information>
      <HeaderText>SECURITY</HeaderText>
      <Information>
        BFH operates secure data networks protected by industry-standard firewall and password protection systems. Our
        security and privacy policies are periodically reviewed and enhanced as necessary and only authorised
        individuals have access to the information provided by our customers.
      </Information>
      <HeaderText>YOUR CONSENT</HeaderText>
      <Information>
        By using this site, you consent to the collection and use of this information by BFH. If we decide to change our
        privacy policy, we will post those changes on this page so that you are always aware of what information we
        collect, how we use it, and under what circumstances we disclose it.
      </Information>
      <HeaderText>SOCIAL MEDIA</HeaderText>
      <Information>
        Our social media pages, (Facebook, Twitter, Instagram and others), are open to comments from all our fans and we
        welcome your feedback, pictures and videos.
      </Information>
      <Information>
        We will remove, without notification, any posts or comments that we deem to be inaccurate, offensive or
        inappropriate – this can include comments posted en masse, comments that are not relevant to the post, use of
        profanities, personal insults and text entirely in capitals.
      </Information>
      <Information>
        If we receive a complaint through our Facebook page, we will handle the complaint through our standard procedure
        and will contact you directly to progress further. For your privacy, we will never comment on individual
        bookings through a public forum.
      </Information>
      <HeaderText>WHAT RIGHTS DO YOU HAVE WITH RESPECT TO YOUR PERSONAL INFORMATION?</HeaderText>
      <Information>
        Under the General Data Protection Regulation, you have certain rights with respect to your personal information,
        including the right to request access to your personal information.
      </Information>
      <Information>
        If you believe that the information, we hold about you is inaccurate and requires correction, please contact us
        using the details below.
      </Information>
      <Information>
        Should you wish to obtain a copy of the personal information that we hold on you, please contact us using the
        details below. We may require further information in order to respond to your request (for instance, evidence of
        your identity and information to enable us to locate the specific personal information you require).
      </Information>
      <HeaderText>COMPLAINTS</HeaderText>
      <Information>
        If you have a complaint regarding the use of your personal information, please contact us using the details
        below:
      </Information>
      <AddressBlock>
        <Address>Data Protection Officer</Address>
        <Address>Breakfree Holidays Ltd</Address>
        <Address>42 Phoenix Court</Address>
        <Address>Hawkins Road</Address>
        <Address>Colchester</Address>
        <Address>CO2 8JY</Address>
      </AddressBlock>
    </ContentWidthContainer>
  </>
);
